import React, { useState, useEffect } from 'react';
import {
    getRundschreibenUpdates,
    getBaFinRundschreiben,
    getBundesbankRundschreiben,
    checkRundschreibenFiles,
    handleSecureDownload,
    getRundschreibenAIAnalysis
} from '../../../../../services/api';
import './ArchivSection.css';

const ArchivSection = () => {
    const [updateInfo, setUpdateInfo] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null);
    const [selectedYear, setSelectedYear] = useState('all');
    const [archivData, setArchivData] = useState(null);
    const [archivLoading, setArchivLoading] = useState(false);
    const [archivError, setArchivError] = useState(null);

    // States für KI-Auswertung
    const [expandedAI, setExpandedAI] = useState({});
    const [aiData, setAiData] = useState({});
    const [loadingAI, setLoadingAI] = useState({});

    // Verfügbare Jahre extrahieren
    const [availableYears, setAvailableYears] = useState([]);

    // API-Call zum Laden der Update-Informationen
    const fetchUpdateInfo = async () => {
        try {
            const result = await getRundschreibenUpdates();
            if (result.success) {
                setUpdateInfo(result.data);
            }
        } catch (err) {
            console.error('Fehler beim Laden der Update-Informationen:', err);
        }
    };

    // Funktion zum Laden der Archiv-Daten
    const fetchArchivData = async (source, year = 'all') => {
        try {
            setArchivLoading(true);
            setArchivError(null);

            let result;
            if (source === 'bafin') {
                result = await getBaFinRundschreiben();
            } else if (source === 'bundesbank') {
                result = await getBundesbankRundschreiben();
            }

            if (result.success) {
                // Sortiere nach Datum
                const sortedData = result.data.data.sort((a, b) => {
                    const dateA = parseGermanDate(a.datum);
                    const dateB = parseGermanDate(b.datum);
                    return dateB - dateA;
                });

                // Extrahiere verfügbare Jahre
                const years = [...new Set(sortedData.map(item => {
                    const date = parseGermanDate(item.datum);
                    return date.getFullYear();
                }))].sort((a, b) => b - a);
                setAvailableYears(years);

                // Filtere nach Jahr wenn ausgewählt
                let filteredData = sortedData;
                if (year !== 'all') {
                    filteredData = sortedData.filter(item => {
                        const date = parseGermanDate(item.datum);
                        return date.getFullYear() === parseInt(year);
                    });
                }

                // Nimm alles NACH den ersten 15 für das Archiv
                const archivItems = year === 'all' ? filteredData.slice(15) : filteredData;

                // Für jedes Rundschreiben: Prüfe verfügbare Dateien und KI-Auswertung
                const enrichedData = await Promise.all(archivItems.map(async (item) => {
                    const fileInfo = await checkAvailableFiles(item.ordner, source);
                    return {
                        ...item,
                        hasDownloadableFiles: fileInfo.hasDownloadableFiles,
                        hasAIAnalysis: fileInfo.hasAIAnalysis,
                        downloadableFiles: fileInfo.downloadableFiles
                    };
                }));

                setArchivData({
                    ...result.data,
                    data: enrichedData,
                    source: source,
                    year: year,
                    totalCount: year === 'all' ? sortedData.length : filteredData.length
                });
            } else {
                throw new Error(result.error || 'Fehler beim Laden der Archiv-Daten');
            }
        } catch (err) {
            console.error(`Fehler beim Laden der ${source} Archiv-Daten:`, err);
            setArchivError(err.message);
        } finally {
            setArchivLoading(false);
        }
    };

    // Prüft verfügbare Dateien für ein Rundschreiben
    const checkAvailableFiles = async (ordnerPfad, source) => {
        if (!ordnerPfad) {
            return { hasDownloadableFiles: false, hasAIAnalysis: false, downloadableFiles: [] };
        }

        try {
            const result = await checkRundschreibenFiles(source, ordnerPfad);
            return result;
        } catch (error) {
            //console.log(`Dateicheck für ${ordnerPfad} fehlgeschlagen:`, error);
            return { hasDownloadableFiles: false, hasAIAnalysis: false, downloadableFiles: [] };
        }
    };

    // Lädt KI-Auswertung für ein Rundschreiben
    const loadAIAnalysis = async (item, source) => {
        const itemKey = `${source}_${item.nummer}`;

        try {
            setLoadingAI(prev => ({ ...prev, [itemKey]: true }));

            const auswertungFolder = source === 'bafin' ? 'auswertung_bafin_gesamt' : 'auswertung_buba_gesamt';

            // FORCE fresh request - add timestamp to prevent caching
            const timestamp = Date.now();
            const result = await getRundschreibenAIAnalysis(source, item.ordner, auswertungFolder, `auswertung.txt?t=${timestamp}`);

            if (result.success) {
                setAiData(prev => ({
                    ...prev,
                    [itemKey]: result.data
                }));
                setExpandedAI(prev => ({
                    ...prev,
                    [itemKey]: true
                }));
            } else {
                setAiData(prev => ({
                    ...prev,
                    [itemKey]: { error: result.error || 'Auswertung nicht verfügbar' }
                }));
            }
        } catch (error) {
            console.error('Error loading AI analysis:', error);
            setAiData(prev => ({
                ...prev,
                [itemKey]: { error: 'Fehler beim Laden der Auswertung' }
            }));
        } finally {
            setLoadingAI(prev => ({ ...prev, [itemKey]: false }));
        }
    };

    // Download von gefilterten Dateien
    const handleDownload = async (item, source) => {
        if (!item.hasDownloadableFiles) return;

        try {
            const result = await handleSecureDownload(source, item.ordner, item.nummer);

            if (!result.success) {
                alert(`Download fehlgeschlagen: ${result.error}`);
            }
        } catch (error) {
            console.error('Download error:', error);
            alert('Download-Fehler aufgetreten');
        }
    };

    // Hilfsfunktion zum Parsen des deutschen Datums
    const parseGermanDate = (dateString) => {
        if (!dateString) return new Date(0);
        const [day, month, year] = dateString.split('.');
        return new Date(year, month - 1, day);
    };

    useEffect(() => {
        fetchUpdateInfo();
    }, []);

    const handleArchivItemClick = (source) => {
        if (selectedSource === source) {
            setSelectedSource(null);
            setArchivData(null);
            setSelectedYear('all');
            setAvailableYears([]);
            return;
        }

        setSelectedSource(source);
        setSelectedYear('all');
        fetchArchivData(source, 'all');
    };

    const handleYearChange = (year) => {
        setSelectedYear(year);
        if (selectedSource) {
            fetchArchivData(selectedSource, year);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'available': return '🟢';
            case 'error': return '🟡';
            case 'unavailable': return '🔴';
            default: return '⚪';
        }
    };

    // Verbesserte KI-Auswertung Render-Funktion (gleich wie in NeuigkeitenSection)
    const renderAIAnalysis = (aiAnalysis) => {
        if (aiAnalysis.error) {
            return (
                <div className="ai-error">
                    <span>⚠️ {aiAnalysis.error}</span>
                </div>
            );
        }

        return (
            <div className="ai-analysis-content">
                {aiAnalysis.zusammenfassung && (
                    <div className="ai-section">
                        <h5>Zusammenfassung</h5>
                        <p>{aiAnalysis.zusammenfassung}</p>
                    </div>
                )}

                {aiAnalysis.empfehlungen && Array.isArray(aiAnalysis.empfehlungen) && (
                    <div className="ai-section">
                        <h5>Empfehlungen</h5>
                        <ul>
                            {aiAnalysis.empfehlungen.map((empfehlung, idx) => (
                                <li key={idx}>{empfehlung}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {aiAnalysis.zuordnung && typeof aiAnalysis.zuordnung === 'object' && (
                    <div className="ai-section">
                        <h5>Zuordnung zu Institutsarten</h5>
                        <div className="zuordnung-grid">
                            {Object.entries(aiAnalysis.zuordnung).map(([institut, beschreibung]) => {
                                // Dreistufige Logik für Betroffenheit
                                const text = beschreibung.toLowerCase();

                                let statusType = 'nicht-betroffen';
                                let statusIcon = '○';

                                if (text.includes('nicht betroffen') ||
                                    text.includes('keine relevanz') ||
                                    text.includes('keine adressierten anforderungen') ||
                                    text.includes('werden nicht erwähnt') ||
                                    text.includes('sind nicht angesprochen') ||
                                    text.includes('keine speziellen vorgaben') ||
                                    text.includes('keine regelungen')) {
                                    statusType = 'nicht-betroffen';
                                    statusIcon = '○';
                                }
                                else if ((text.includes('betroffen') && !text.includes('nicht')) ||
                                    text.includes('müssen') ||
                                    text.includes('sind verpflichtet') ||
                                    text.includes('unterliegen') ||
                                    text.includes('relevante anforderungen') ||
                                    text.includes('anforderungen für institute') ||
                                    text.includes('vorgaben für') ||
                                    text.includes('regelungen für')) {
                                    statusType = 'betroffen';
                                    statusIcon = '●';
                                }
                                else if (text.includes('relevant') && !text.includes('keine relevanz')) {
                                    statusType = 'unklar';
                                    statusIcon = '◐';
                                }

                                return (
                                    <div key={institut} className={`zuordnung-item ${statusType}`}>
                                        <div className="institut-name">
                                            <span className={`status-indicator ${statusType}`}>
                                                {statusIcon}
                                            </span>
                                            {institut.toUpperCase()}
                                        </div>
                                        <div className="institut-beschreibung">
                                            {beschreibung}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {aiAnalysis.relevanz && (
                    <div className="ai-section">
                        <h5>Relevanz-Einschätzung</h5>
                        <div className={`relevanz-badge ${aiAnalysis.relevanz.toLowerCase()}`}>
                            {aiAnalysis.relevanz}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Archiv-Liste rendern
    const renderArchivList = () => {
        if (!selectedSource) return null;

        return (
            <div className="archiv-content">
                <div className="archiv-header">
                    <h3>
                        {selectedSource === 'bafin' ? 'BaFin' : 'Bundesbank'} Rundschreiben - Archiv
                        {selectedYear !== 'all' && ` (${selectedYear})`}
                    </h3>
                    <div className="archiv-controls">
                        <select
                            className="year-filter"
                            value={selectedYear}
                            onChange={(e) => handleYearChange(e.target.value)}
                        >
                            <option value="all">Alle Jahre</option>
                            {availableYears.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                        <button
                            className="close-button"
                            onClick={() => {
                                setSelectedSource(null);
                                setArchivData(null);
                                setSelectedYear('all');
                                setAiData({});
                                setExpandedAI({});
                            }}
                            title="Schließen"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {archivLoading && (
                    <div className="loading-state">
                        <span>Lade Archiv-Daten...</span>
                    </div>
                )}

                {archivError && (
                    <div className="error-state">
                        <span>Fehler: {archivError}</span>
                        <button
                            className="retry-button"
                            onClick={() => fetchArchivData(selectedSource, selectedYear)}
                        >
                            Erneut versuchen
                        </button>
                    </div>
                )}

                {archivData && !archivLoading && (
                    <div className="archiv-list">
                        <div className="archiv-stats">
                            <span>{archivData.data.length} Einträge gefunden</span>
                            {selectedYear === 'all' && (
                                <span>(ohne die neuesten 15)</span>
                            )}
                        </div>

                        {archivData.data.map((item, index) => {
                            const itemKey = `${selectedSource}_${item.nummer}`;
                            const isAIExpanded = expandedAI[itemKey];
                            const isAILoading = loadingAI[itemKey];
                            const currentAIData = aiData[itemKey];

                            return (
                                <div key={index} className="archiv-item">
                                    <div className="item-header">
                                        <div className="item-number">
                                            {item.nummer || "-"}
                                        </div>
                                        <div className="item-date">
                                            {item.datum}
                                        </div>
                                    </div>

                                    <div className="item-content">
                                        <h4 className="item-title">
                                            {item.titel}
                                        </h4>

                                        {selectedSource === 'bundesbank' && item.untertitel && (
                                            <p className="item-subtitle">
                                                {item.untertitel}
                                            </p>
                                        )}

                                        {selectedSource === 'bafin' && item.bereich && (
                                            <div className="item-meta">
                                                <span className="meta-label">Bereich:</span>
                                                <span className="meta-value">{item.bereich}</span>
                                            </div>
                                        )}

                                        {selectedSource === 'bafin' && item.thema && (
                                            <div className="item-meta">
                                                <span className="meta-label">Thema:</span>
                                                <span className="meta-value">{item.thema}</span>
                                            </div>
                                        )}

                                        {selectedSource === 'bafin' && item.zusammenfassung && (
                                            <p className="item-summary">
                                                {item.zusammenfassung}
                                            </p>
                                        )}
                                    </div>

                                    <div className="item-actions">
                                        <button
                                            className={`download-button ${!item.hasDownloadableFiles ? 'disabled' : ''}`}
                                            onClick={() => handleDownload(item, selectedSource)}
                                            disabled={!item.hasDownloadableFiles}
                                            title={item.hasDownloadableFiles ? 'Dokumente herunterladen (HTML/PDF)' : 'Keine downloadbaren Dokumente verfügbar'}
                                        >
                                            📄 Dokumente ({item.downloadableFiles?.length || 0})
                                        </button>

                                        <button
                                            className={`ai-button ${!item.hasAIAnalysis ? 'disabled' : ''} ${isAIExpanded ? 'active' : ''}`}
                                            onClick={() => loadAIAnalysis(item, selectedSource)}
                                            disabled={!item.hasAIAnalysis || isAILoading}
                                            title={item.hasAIAnalysis ? 'KI-basierte Auswertung anzeigen' : 'Keine KI-Auswertung verfügbar'}
                                        >
                                            {isAILoading ? '⏳' : '🪄'} KI-Auswertung
                                        </button>
                                    </div>

                                    {/* KI-Auswertung Bereich */}
                                    {isAIExpanded && currentAIData && (
                                        <div className="ai-analysis-section">
                                            <div className="ai-analysis-header">
                                                <h5>KI-basierte Analyse</h5>
                                                <button
                                                    className="collapse-button"
                                                    onClick={() => setExpandedAI(prev => ({
                                                        ...prev,
                                                        [itemKey]: false
                                                    }))}
                                                >
                                                    ↑
                                                </button>
                                            </div>
                                            {renderAIAnalysis(currentAIData)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {archivData.data.length === 0 && (
                            <div className="no-data">
                                {selectedYear !== 'all'
                                    ? `Keine Rundschreiben für das Jahr ${selectedYear} gefunden.`
                                    : 'Keine weiteren Rundschreiben im Archiv gefunden.'
                                }
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="archiv-section">
            <div className="section-header">
                <h2>Archiv</h2>
            </div>

            <div className="archiv-table">
                <div className="table-header">
                    <div className="col-header">Rundschreiben</div>
                    <div className="col-header">Merkblätter</div>
                    <div className="col-header">Gesetze / Richtlinien</div>
                </div>

                <div className="table-row">
                    <div className="table-cell">
                        <div className="archiv-items">
                            <button
                                className={`archiv-item-link ${selectedSource === 'bafin' ? 'active' : ''}`}
                                onClick={() => handleArchivItemClick('bafin')}
                                disabled={updateInfo?.bafin.status !== 'available'}
                            >
                                <span className="archiv-item-title">
                                    {getStatusIcon(updateInfo?.bafin.status || 'unknown')}
                                    &nbsp;BaFin Rundschreiben
                                </span>
                                <span className="archiv-indicator">
                                    {selectedSource === 'bafin' ? '↓' : '→'}
                                </span>
                            </button>

                            <button
                                className={`archiv-item-link ${selectedSource === 'bundesbank' ? 'active' : ''}`}
                                onClick={() => handleArchivItemClick('bundesbank')}
                                disabled={updateInfo?.bundesbank.status !== 'available'}
                            >
                                <span className="archiv-item-title">
                                    {getStatusIcon(updateInfo?.bundesbank.status || 'unknown')}
                                    &nbsp;Bundesbank Rundschreiben
                                </span>
                                <span className="archiv-indicator">
                                    {selectedSource === 'bundesbank' ? '↓' : '→'}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="table-cell">
                        <span className="no-updates">...</span>
                    </div>

                    <div className="table-cell">
                        <span className="no-updates">...</span>
                    </div>
                </div>
            </div>

            {renderArchivList()}
        </div>
    );
};

export default ArchivSection;